import * as React from "react";
import { useState } from "react";
import toast, { Toaster } from "react-hot-toast";

/**
 * Post-event feedback form (workshop / main congress).
 *
 * Neither the questions nor the section structure are defined here -- they come
 * from FeedbackForm::DEFINITIONS on the Ruby side, so the admin report columns
 * and this page can never drift apart. This file only knows how to render the
 * five question types and how to honour a question's `depends_on`.
 */

type QuestionType = "scale" | "choice" | "multi" | "string" | "text";

type Answer = string | number | string[];

interface Dependency {
  key: string;
  value: string;
}

interface Question {
  key: string;
  type: QuestionType;
  label: string;
  options?: string[];
  option_groups?: Record<string, string[]>;
  required: boolean;
  depends_on?: Dependency;
}

interface Section {
  title?: string;
  subtitle?: string;
  legend?: string;
  questions: Question[];
}

interface FeedbackFormProps {
  token: string;
  url: string;
  formKey: string;
  title: string;
  subtitle: string;
  intro: string;
  sections: Section[];
  dashboardUrl: string;
}

const SCALE = [1, 2, 3, 4, 5];

const isAnswered = (value: Answer | undefined) =>
  Array.isArray(value) ? value.length > 0 : !!String(value ?? "").trim();

const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
  <svg
    viewBox="0 0 20 20"
    aria-hidden="true"
    className={`h-8 w-8 transition-all duration-200 ease-out ${
      filled
        ? "scale-110 text-amber-400 drop-shadow-sm"
        : "scale-100 text-gray-300"
    }`}
    fill="currentColor"
  >
    <path d="M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.42 4.38a1 1 0 00.95.69h4.6c.97 0 1.37 1.24.59 1.81l-3.73 2.7a1 1 0 00-.36 1.12l1.42 4.38c.3.92-.76 1.68-1.54 1.11l-3.73-2.7a1 1 0 00-1.18 0l-3.72 2.7c-.79.57-1.85-.19-1.55-1.11l1.42-4.38a1 1 0 00-.36-1.12l-3.73-2.7c-.78-.57-.38-1.81.6-1.81h4.6a1 1 0 00.95-.69L9.05 2.93z" />
  </svg>
);

/**
 * 1-5 star scale. Hover previews the rating, so `hovered` wins over the stored
 * value while the pointer is inside the row.
 */
const ScaleInput: React.FC<{
  value?: number;
  onChange: (value: number) => void;
}> = ({ value, onChange }) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const active = hovered ?? value ?? 0;

  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHovered(null)}
    >
      {SCALE.map((score) => (
        <button
          key={score}
          type="button"
          aria-label={`${score} out of 5`}
          aria-pressed={value === score}
          className="cursor-pointer rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          onMouseEnter={() => setHovered(score)}
          onFocus={() => setHovered(score)}
          onBlur={() => setHovered(null)}
          onClick={() => onChange(score)}
        >
          <StarIcon filled={score <= active} />
        </button>
      ))}
      <span className="ml-2 min-w-14 text-sm text-gray-500">
        {value ? `${value} / 5` : ""}
      </span>
    </div>
  );
};

const optionClass = (selected: boolean) =>
  `flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 text-sm transition-colors duration-150 ${
    selected
      ? "border-blue-500 bg-blue-50 text-gray-900"
      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
  }`;

const FeedbackForm: React.FC<FeedbackFormProps> = ({
  token,
  url,
  title,
  subtitle,
  intro,
  sections,
  dashboardUrl,
}) => {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [confirming, setConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setAnswer = (key: string, value: Answer) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const toggleChoice = (key: string, option: string) =>
    setAnswers((prev) => {
      const current = Array.isArray(prev[key]) ? (prev[key] as string[]) : [];
      return {
        ...prev,
        [key]: current.includes(option)
          ? current.filter((item) => item !== option)
          : [...current, option],
      };
    });

  const isVisible = (question: Question) =>
    !question.depends_on ||
    String(answers[question.depends_on.key] ?? "") === question.depends_on.value;

  // Only what the delegate can actually see is submitted. Answering "Yes",
  // filling the dependent questions in and switching back to "No" must not send
  // those answers -- the server drops them too, this just keeps the two agreeing.
  const visibleQuestions = sections
    .flatMap((section) => section.questions)
    .filter(isVisible);

  const payload = Object.fromEntries(
    visibleQuestions
      .filter((question) => isAnswered(answers[question.key]))
      .map((question) => [question.key, answers[question.key]]),
  );

  const missing = visibleQuestions.filter(
    (question) => question.required && !isAnswered(answers[question.key]),
  );

  // Validate before opening the modal, so the confirmation step never turns out
  // to be a dead end.
  const requestConfirm = () => {
    if (missing.length) {
      toast.error(
        `Please answer all required questions (${missing.length} remaining).`,
      );
      document
        .getElementById(`question-${missing[0].key}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setConfirming(true);
  };

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ answers: payload }),
      });

      // A lapsed session redirects to the login page, and fetch follows the
      // redirect: the response is 200 with HTML, so response.ok is true and
      // .json() throws on "<". These forms are long, so failing here with a
      // parser error would throw away everything the delegate typed. Bail out
      // without navigating -- the answers stay on the page, ready to resend
      // once they have logged back in.
      if (!response.headers.get("content-type")?.includes("application/json")) {
        setConfirming(false);
        toast.error(
          "Your session has expired. Log in again in another tab, then submit — your answers are still here.",
        );
        return;
      }

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || "Feedback submitted!");
        window.location.href = result.redirect_url || dashboardUrl;
      } else {
        setConfirming(false);
        toast.error(result.error || "Submission failed.");
      }
    } catch (error: any) {
      setConfirming(false);
      toast.error(error?.message || "Submission failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCheckboxes = (question: Question, options: string[]) => (
    <div className="flex flex-col gap-2">
      {options.map((option) => {
        const selected =
          Array.isArray(answers[question.key]) &&
          (answers[question.key] as string[]).includes(option);
        return (
          <label key={option} className={optionClass(selected)}>
            <input
              type="checkbox"
              checked={selected}
              onChange={() => toggleChoice(question.key, option)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600"
            />
            {option}
          </label>
        );
      })}
    </div>
  );

  const renderInput = (question: Question) => {
    const value = answers[question.key];

    switch (question.type) {
      case "scale":
        return (
          <ScaleInput
            value={typeof value === "number" ? value : undefined}
            onChange={(score) => setAnswer(question.key, score)}
          />
        );

      case "choice":
        return (
          <div className="flex flex-col gap-2">
            {(question.options || []).map((option) => (
              <label key={option} className={optionClass(value === option)}>
                <input
                  type="radio"
                  name={question.key}
                  value={option}
                  checked={value === option}
                  onChange={() => setAnswer(question.key, option)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600"
                />
                {option}
              </label>
            ))}
          </div>
        );

      case "multi":
        // Grouped options keep their track heading (MSN / MSC / BNC).
        if (question.option_groups) {
          return (
            <div className="flex flex-col gap-5">
              {Object.entries(question.option_groups).map(([group, options]) => (
                <div key={group}>
                  <h4 className="mb-2 text-xs font-bold tracking-wide text-gray-500 uppercase">
                    {group}
                  </h4>
                  {renderCheckboxes(question, options)}
                </div>
              ))}
            </div>
          );
        }
        return renderCheckboxes(question, question.options || []);

      case "text":
        return (
          <textarea
            rows={4}
            value={String(value ?? "")}
            onChange={(event) => setAnswer(question.key, event.target.value)}
            className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        );

      default:
        return (
          <input
            type="text"
            value={String(value ?? "")}
            onChange={(event) => setAnswer(question.key, event.target.value)}
            className="w-full rounded-md border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        );
    }
  };

  return (
    <>
      <Toaster toastOptions={{ className: "cursor-pointer ps-3" }} />

      <div className="min-h-screen bg-gray-100 py-8">
        <div className="mx-auto max-w-3xl px-4">
          <div className="mb-6 rounded-lg bg-white p-6 shadow">
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <p className="mt-1 text-sm font-medium text-gray-500">{subtitle}</p>
            <p className="mt-4 text-sm leading-relaxed text-gray-600">{intro}</p>
          </div>

          {sections.map((section, sectionIndex) => {
            const shown = section.questions.filter(isVisible);
            if (!shown.length) return null;

            return (
              <div key={section.title ?? sectionIndex} className="mb-6">
                {(section.title || section.legend) && (
                  <div className="mb-3 rounded-lg bg-white p-5 shadow">
                    {section.title && (
                      <h2 className="text-lg font-bold text-gray-900">
                        {section.title}
                      </h2>
                    )}
                    {section.subtitle && (
                      <p className="mt-1 text-sm text-gray-600">
                        {section.subtitle}
                      </p>
                    )}
                    {section.legend && (
                      <p className="mt-3 rounded-md bg-gray-50 px-4 py-2 text-xs text-gray-500">
                        {section.legend}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {shown.map((question, index) => (
                    <div
                      key={question.key}
                      id={`question-${question.key}`}
                      className="rounded-lg bg-white p-6 shadow"
                    >
                      <label className="mb-4 flex gap-2 text-sm font-semibold text-gray-900">
                        <span className="text-gray-400">{index + 1}.</span>
                        <span>
                          {question.label}
                          {question.required && (
                            <span className="ml-1 text-red-500">*</span>
                          )}
                        </span>
                      </label>
                      {renderInput(question)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <a
              href={dashboardUrl}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </a>
            <button
              type="button"
              onClick={requestConfirm}
              className="inline-flex cursor-pointer items-center justify-center rounded-md bg-[#395eaa] px-6 py-3 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:brightness-110"
            >
              Submit Feedback
            </button>
          </div>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">
              Submit your feedback?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              You can only submit this form once. Your answers cannot be changed
              afterwards.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setConfirming(false)}
                className="inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Go back
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={submit}
                className="inline-flex cursor-pointer items-center justify-center rounded-md bg-[#395eaa] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50"
              >
                {isSubmitting ? "Submitting..." : "Yes, submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackForm;
